
import React, { useState } from "react";
import {
  FaCoffee,
  FaHeart,
  FaRocket,
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaExternalLinkAlt,
} from "react-icons/fa";

const STORAGE_KEY = "monappliclub_developer_support_collapsed";

// Ancienne clé utilisée pour masquer la carte pendant 30 jours.
const OLD_STORAGE_KEY =
  "monappliclub_developer_support_hidden_until";

const SUPPORT_OPTIONS = [
  {
    amount: 2,
    label: "2 €",
    title: "Un petit café",
    Icon: FaCoffee,
    url: "https://buy.stripe.com/4gM8wOfn5fDo7zufRU5AQ07",
  },
  {
    amount: 5,
    label: "5 €",
    title: "Un coup de pouce",
    Icon: FaHeart,
    url: "https://buy.stripe.com/cN2aFtaqL5fC1AkcMR",
  },
  {
    amount: 10,
    label: "10 €",
    title: "Un grand soutien",
    Icon: FaRocket,
    url: "https://buy.stripe.com/dRm00ib6PdvgaLG6hk5AQ08",
  },
];

function getInitialCollapsedState() {
  try {
    // Suppression de l'ancien mécanisme de masquage.
    localStorage.removeItem(OLD_STORAGE_KEY);

    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export default function DeveloperSupportCard() {
  const [isCollapsed, setIsCollapsed] = useState(
    getInitialCollapsedState
  );

  const toggleCollapsed = () => {
    const nextValue = !isCollapsed;

    setIsCollapsed(nextValue);

    try {
      localStorage.setItem(STORAGE_KEY, String(nextValue));
    } catch {
      // Le composant reste fonctionnel sans localStorage.
    }
  };

  return (
    <section
      aria-label="Soutenir le développeur de MonAppliClub"
      className="mt-5 overflow-hidden rounded-2xl border border-rose-100 bg-gradient-to-r from-white via-rose-50/50 to-blue-50/60 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800"
    >
      <div
        className={`flex flex-col gap-4 ${
          isCollapsed ? "p-3 sm:p-4" : "p-5 sm:p-6"
        } lg:flex-row lg:items-center lg:justify-between`}
      >
        {/* Présentation */}
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`flex shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 ${
              isCollapsed ? "h-9 w-9" : "h-11 w-11"
            }`}
          >
            <FaHeart size={isCollapsed ? 16 : 20} />
          </div>

          <div className="min-w-0">
            <h2 className="text-sm font-extrabold text-slate-900 dark:text-white sm:text-base">
              {isCollapsed
                ? "Un petit coup de pouce au développeur ?"
                : "Vous aimez votre application ?"}
            </h2>

            {isCollapsed ? (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                ☕ Un petit café à 2 € pour soutenir mes créations ?
              </p>
            ) : (
              <>
                <p className="mt-1 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Je développe ces outils avec passion pour
                  faciliter la vie des clubs sportifs. Si vous
                  les appréciez, un petit soutien m'encourage
                  à continuer de les améliorer !
                </p>

                <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <FaLock className="text-emerald-600" />
                  Soutien facultatif · Paiement sécurisé avec Stripe
                </p>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex w-full min-w-0 items-center gap-2 lg:w-auto lg:shrink-0">
          {!isCollapsed && (
            <div className="grid min-w-0 flex-1 grid-cols-3 gap-2 lg:w-[340px]">
              {SUPPORT_OPTIONS.map((option) => {
                const Icon = option.Icon;

                return (
                  <a
                    key={option.amount}
                    href={option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={option.title}
                    aria-label={`${option.title} : ${option.label}`}
                    className="group flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl border border-slate-200 bg-white px-2 py-3 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-rose-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-rose-500"
                  >
                    <Icon className="text-base text-rose-500" />

                    <span className="text-lg font-extrabold text-slate-900 dark:text-white">
                      {option.label}
                    </span>

                    <span className="text-[10px] leading-4 text-slate-500 dark:text-slate-400 sm:text-xs">
                      {option.title}
                    </span>

                    <FaExternalLinkAlt
                      size={9}
                      className="mt-1 text-slate-400"
                    />
                  </a>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!isCollapsed}
            aria-label={
              isCollapsed
                ? "Développer la carte de soutien"
                : "Réduire la carte de soutien"
            }
            title={
              isCollapsed
                ? "Développer"
                : "Réduire"
            }
            className="flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white lg:self-center"
          >
            {isCollapsed ? (
              <FaChevronDown size={13} />
            ) : (
              <FaChevronUp size={13} />
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
